import { useMemo, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useQuickSearch } from "../../../../hooks";
import { useCreateScheduleDraft } from "./useCreateScheduleDraft";
import { useDeleteScheduleDraft } from "./useDeleteScheduleDraft";
import { useScheduleDraftList } from "./useScheduleDraftList";

const STATUS_LABELS = {
    EDITING: "草稿/部分发布",
    FINALIZED: "发布完成",
    ARCHIVED: "已归档",
};

const SOURCE_TYPE_LABELS = {
    BLANK: "空白航班计划",
    FROM_V1: "旧版导入",
    CLONE: "复制航班计划",
};

function normalizeCreateSubmission(submission = {}) {
    if (submission.createPayload) {
        return submission;
    }
    return { createPayload: submission, pendingSourceImport: null };
}

function resolveErrorMessage(error, fallback) {
    return error?.response?.data?.detail || error?.response?.data?.message || error?.message || fallback;
}

export function useScheduleDraftListPageState() {
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [filterParams, setFilterParams] = useState({});
    const [createOpen, setCreateOpen] = useState(false);

    const listQuery = useScheduleDraftList({
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    const createDraftMutation = useCreateScheduleDraft({
        onError: (error) => {
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || "创建航班计划失败";
            message.error(errorMessage);
        },
    });

    const deleteDraftMutation = useDeleteScheduleDraft();

    const quickSearch = useQuickSearch({
        setFilterParams,
        setPagination,
        paramKey: "search",
    });

    const summaryItems = useMemo(() => {
        const items = [];

        if (filterParams.status) {
            items.push({ label: "状态", value: STATUS_LABELS[filterParams.status] || filterParams.status });
        }

        if (filterParams.source_type) {
            items.push({ label: "来源", value: SOURCE_TYPE_LABELS[filterParams.source_type] || filterParams.source_type });
        }

        if (filterParams.search) {
            items.push({ label: "搜索", value: filterParams.search, hidden: true });
        }

        return items;
    }, [filterParams]);

    const handleReset = () => {
        quickSearch.clear();
        setFilterParams({});
        setPagination((current) => ({ ...current, current: 1 }));
    };

    const handleFilterChange = (nextPartialFilters) => {
        setFilterParams((previous) => ({
            ...(previous.search ? { search: previous.search } : {}),
            ...(nextPartialFilters.status ? { status: nextPartialFilters.status } : {}),
            ...(nextPartialFilters.source_type ? { source_type: nextPartialFilters.source_type } : {}),
        }));
        setPagination((current) => ({ ...current, current: 1 }));
    };

    const handleCreateDraft = async (submission) => {
        try {
            const { createPayload } = normalizeCreateSubmission(submission);
            const createdDraft = await createDraftMutation.mutateAsync(createPayload);
            const isV1Import = createPayload.source_type === "FROM_V1";
            message.success(isV1Import ? "航班计划已创建并导入旧版航班" : "航班计划已创建");
            setCreateOpen(false);
            await listQuery.refetch();
            navigate(`/flightSchedule/v2/drafts/${createdDraft.id}`);
        } catch {
            return;
        }
    };

    const handleRefresh = () => {
        listQuery.refetch();
    };

    const handleDeleteDraft = async (draftId) => {
        try {
            await deleteDraftMutation.mutateAsync(draftId);
            message.success("航班计划已删除");
            await listQuery.refetch();
            return true;
        } catch (error) {
            message.error(resolveErrorMessage(error, "删除航班计划失败"));
            return false;
        }
    };

    const handleBatchDeleteDrafts = async (draftIds = []) => {
        if (!draftIds.length) {
            message.warning("请选择要删除的航班计划");
            return { successCount: 0, failureCount: 0 };
        }

        const results = await Promise.allSettled(draftIds.map((draftId) => deleteDraftMutation.mutateAsync(draftId)));
        const successCount = results.filter((result) => result.status === "fulfilled").length;
        const failureCount = results.length - successCount;

        if (successCount > 0) {
            message.success(`已删除 ${successCount} 个航班计划`);
        }
        if (failureCount > 0) {
            const firstError = results.find((result) => result.status === "rejected")?.reason;
            message.error(resolveErrorMessage(firstError, `${failureCount} 个航班计划删除失败`));
        }

        await listQuery.refetch();
        return { successCount, failureCount };
    };

    const handleCopyDraftId = async (draftId) => {
        try {
            await navigator.clipboard.writeText(draftId);
            message.success("航班计划 ID 已复制");
        } catch {
            message.warning(`请手动复制航班计划 ID: ${draftId}`);
        }
    };

    const handleTableChange = (nextPagination) => {
        setPagination({
            current: nextPagination.current,
            pageSize: nextPagination.pageSize,
        });
    };

    const openDraft = (draftId) => {
        navigate(`/flightSchedule/v2/drafts/${draftId}`);
    };

    return {
        data: listQuery.data,
        isLoading: listQuery.isLoading,
        isFetching: listQuery.isFetching,
        pagination,
        createOpen,
        createDraftLoading: createDraftMutation.isLoading,
        deleteDraftLoading: deleteDraftMutation.isLoading,
        filterParams,
        quickSearchInputProps: quickSearch.inputProps,
        summaryItems,
        openDraft,
        openCreateDialog: () => setCreateOpen(true),
        closeCreateDialog: () => setCreateOpen(false),
        handleCreateDraft,
        handleDeleteDraft,
        handleBatchDeleteDrafts,
        handleCopyDraftId,
        handleRefresh,
        handleReset,
        handleStatusChange: (status) => handleFilterChange({
            status,
            source_type: filterParams.source_type || "",
        }),
        handleSourceTypeChange: (sourceType) => handleFilterChange({
            status: filterParams.status || "",
            source_type: sourceType,
        }),
        handleTableChange,
    };
}

export default useScheduleDraftListPageState;
